import { Controller, useFormContext } from "react-hook-form";
import { Calendar } from "primereact/calendar";
import { classNames } from "primereact/utils";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { AppTooltip } from "../../../tooltip/AppTooltip";
import { useAppContext } from "../../../../../AppContext";
import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
import { useInputDateField } from "./hooks/useInputDateField";
// The field frame, label and text deliberately come from DefaultInputField's stylesheet, so
// a date sits in a form looking exactly like the text and number fields around it.
import inputStyles from "../InputField/DefaultInputField.module.scss";
import styles from "./InputDateField.module.scss";

interface IInputDateFieldProps {
  className?: string;
  name: string;
  label?: string;
  placeholder?: string;
  tooltip?: string;
  required?: boolean;
  disabled?: boolean;
  width?: string;
  /** Earliest selectable day; days before it are greyed out in the picker. */
  minDate?: Date;
  /** Latest selectable day; days after it are greyed out in the picker. */
  maxDate?: Date;
  /** Shows a clear (×) button while a date is set, for fields where "no date" is meaningful. */
  clearable?: boolean;
}

/**
 * A date-only field styled like DefaultInputField. Use it inside forms built from
 * DefaultInputField. DefaultDateField is the taller, button-style picker used by the planner
 * and wizards; this one is for compact dialog forms.
 */
export const InputDateField = ({
  className,
  name,
  label,
  placeholder = "дд/мм/гггг",
  tooltip,
  required = false,
  disabled,
  width,
  minDate,
  maxDate,
  clearable = false,
}: IInputDateFieldProps) => {
  const { control } = useFormContext();
  const { isDesktopOrTablet } = useAppContext();
  const { isFocused, onFocus, onBlur } = useInputDateField();

  const resolvedWidth = width ?? (isDesktopOrTablet ? "600px" : dvSize("80dvw"));

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div
          style={{ width: resolvedWidth, maxWidth: resolvedWidth }}
          className={classNames(className, inputStyles.root, styles.root, {
            [inputStyles.root_disabled]: disabled,
          })}
        >
          <RenderWhen condition={!!label}>
            <div
              className={classNames(inputStyles.root_label, {
                [inputStyles.root_label_interacted_with]: isFocused || !!field.value,
                [inputStyles.root_label_error]: !!fieldState.error?.message,
              })}
            >
              {label}
              {required ? "*" : ""}
              <AppTooltip content={tooltip} />
            </div>
          </RenderWhen>

          <div className={classNames(inputStyles.root_input_container, styles.root_row)}>
            <Calendar
              inputId={field.name}
              value={field.value ?? null}
              onChange={(e) => field.onChange(e.value ?? null)}
              onFocus={onFocus}
              onBlur={() => {
                onBlur();
                field.onBlur();
              }}
              dateFormat="dd/mm/yy"
              placeholder={placeholder}
              minDate={minDate}
              maxDate={maxDate}
              disabled={disabled}
              className={styles.root_calendar}
              inputClassName={classNames(inputStyles.root_input, styles.root_calendar_input)}
              // A phone gets the full-screen picker; a desktop gets a popup attached to the
              // body so a dialog's scroll area can never clip it.
              touchUI={!isDesktopOrTablet}
              appendTo={typeof document !== "undefined" ? document.body : undefined}
              readOnlyInput
            />

            <RenderWhen condition={clearable && !!field.value && !disabled}>
              <button
                type="button"
                className={styles.root_icon_button}
                onClick={() => field.onChange(null)}
                aria-label="Изчисти датата"
                title="Изчисти датата"
              >
                <i className="pi pi-times" />
              </button>
            </RenderWhen>

            <i className={classNames("pi pi-calendar", styles.root_icon)} aria-hidden="true" />
          </div>

        </div>
      )}
    />
  );
};
